import {useEffect, useRef} from 'react';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {trackViewCartFromBasket} from '@amblique/king-living/app/analytics/ga4/view-cart';

/**
 * Fire GA4 `view_cart` once per cart flyout open.
 * Prefer waiting for flyout catalog hydrate so we can reuse productsById and
 * avoid a parallel GA4 getProducts (DE-355). Callers must clear
 * `isCatalogPending` after the catalog query settles or errors so tracking
 * is never blocked forever.
 */
export const useViewCartOnFlyoutOpen = ({
  basket,
  currency,
  customer,
  isBasketLoading,
  isCatalogPending = false,
  isOpen,
  productsById = null,
  queryClient = null,
}) => {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const viewCartTrackedRef = useRef(false);
  const basketRef = useRef(basket);
  const productsByIdRef = useRef(productsById);
  const queryClientRef = useRef(queryClient);

  basketRef.current = basket;
  productsByIdRef.current = productsById;
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!isOpen) {
      viewCartTrackedRef.current = false;

      return;
    }

    if (isBasketLoading || isCatalogPending || viewCartTrackedRef.current) {
      return;
    }

    if ((basketRef.current?.productItems?.length ?? 0) > 0 && !api?.shopperProducts) {
      return;
    }

    viewCartTrackedRef.current = true;

    void trackViewCartFromBasket({
      api,
      basket: basketRef.current,
      currency,
      customer,
      getToken: getTokenWhenReady,
      productsById: productsByIdRef.current,
      queryClient: queryClientRef.current,
    });
  }, [
    api,
    currency,
    customer,
    getTokenWhenReady,
    isBasketLoading,
    isCatalogPending,
    isOpen,
    productsById
  ]);
};
